import React, { useCallback, useRef, useState } from "react";
import { commitMutation, graphql } from "react-relay";
import type { OnChangeValue } from "react-select";

import { faExclamationCircle, faHourglassHalf } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { fetchQuery } from "relay-runtime";

import { ReactComponent as NotionLogo } from "~/legacy-assets/images/logos/notion-logo.svg";
import { environment } from "~/models";
import { Button } from "~/ui";
import { Input, Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import { IncludeAIRecapCheckbox } from "../IncludeAIRecapCheckbox";
import { type NotionPage, NotionPageSelect } from "./NotionPageSelect";

import type { NotionSendNotesModalAsyncTaskQuery } from "~/__generated__/NotionSendNotesModalAsyncTaskQuery.graphql";
import type { NotionSendNotesModalMutation as MutationType } from "~/__generated__/NotionSendNotesModalMutation.graphql";

import styles from "./css/NotionSendNotes.module.css";

const SendNoteToNotionMutation = graphql`
	mutation NotionSendNotesModalMutation(
		$title: String
		$noteId: ID!
		$notionPageId: String!
		$isDatabase: Boolean!
		$icon: String
		$includeMeetingRecap: Boolean
	) {
		sendNoteToNotion(
			title: $title
			noteId: $noteId
			notionPageId: $notionPageId
			isDatabase: $isDatabase
			icon: $icon
			includeMeetingRecap: $includeMeetingRecap
		) {
			asyncResultId
		}
	}
`;

const SendNoteToNotionAsyncTaskQuery = graphql`
	query NotionSendNotesModalAsyncTaskQuery($taskId: JSON!) {
		viewer {
			asyncTask(taskId: $taskId) {
				taskId
				state
				info
			}
		}
	}
`;

interface Props {
	noteId: string;
	name: string;
	onClose: () => void;
	trackAnalytics: () => void;
	hasMeetingRecordings: boolean;
}

type View = "form" | "loading" | "complete" | "error" | "timeout";

export const NotionSendNotesModal: React.FunctionComponent<Props> = ({
	noteId,
	name,
	onClose,
	trackAnalytics,
	hasMeetingRecordings,
}) => {
	const [title, setTitle] = useState<string>(`[Fellow] ${name}`);
	const [notionPage, setNotionPage] = useState<OnChangeValue<NotionPage, false>>();
	const taskId = useRef<string | null>();
	const [notionURL, setNotionURL] = useState<string | undefined>();
	const [view, setView] = useState<View>("form");
	const [includeMeetingRecap, setIncludeMeetingRecap] = useState<boolean>(hasMeetingRecordings);

	const refreshStateTimer = useRef(0);

	const startExport = useCallback(() => {
		trackAnalytics();
		setView("loading");

		commitMutation<MutationType>(environment, {
			mutation: SendNoteToNotionMutation,
			variables: {
				title: title,
				noteId: noteId,
				notionPageId: notionPage?.id || "",
				isDatabase: notionPage?.isDb || false,
				icon: "default",
				includeMeetingRecap: includeMeetingRecap,
			},
			onCompleted: ({ sendNoteToNotion }, errors) => {
				function refreshState() {
					fetchQuery<NotionSendNotesModalAsyncTaskQuery>(environment, SendNoteToNotionAsyncTaskQuery, {
						taskId: taskId.current,
					})
						.toPromise()
						.then(resp => {
							const asyncTask = resp?.viewer.asyncTask;
							if (!asyncTask) return;
							const { state, info } = asyncTask;
							if (state == "SUCCESS") {
								setNotionURL(info.notionURL);
								setView("complete");
							} else if (state == "PENDING") {
								refreshStateTimer.current = window.setTimeout(refreshState, 2000);
							} else {
								const errorString: string = asyncTask.info?.error?.message || asyncTask.info?.error?.args || "";
								if (errorString.includes("timed out")) {
									setView("timeout");
								} else {
									setView("error");
								}
							}
						});
				}

				if (errors?.length) {
					setView("error");
				} else if (sendNoteToNotion) {
					taskId.current = sendNoteToNotion.asyncResultId;
					refreshStateTimer.current = window.setTimeout(refreshState, 500);
				}
			},
		});
	}, [trackAnalytics, title, noteId, notionPage?.id, notionPage?.isDb, taskId, includeMeetingRecap]);

	const openNotionURL = useCallback(() => {
		window.open(notionURL);
	}, [notionURL]);

	const onTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setTitle(e.target.value);
	}, []);

	const onPageChange = useCallback((value: OnChangeValue<NotionPage, false>): void => {
		setNotionPage(value);
	}, []);

	const onIncludeMeetingRecapChange = useCallback(() => {
		setIncludeMeetingRecap((prev: boolean) => !prev);
	}, []);

	const formBody = (
		<>
			<div className={styles.form}>
				<label className={styles.title}>Note title:</label>
				<Input
					value={title}
					className={styles.titleInput}
					onChange={onTitleChange}
					placeholder="Write a custom title for your Notion post..."
					maxLength={255}
					name="notion_note_title"
				/>
			</div>
			<div className={styles.form}>
				<label className={styles.title}>Parent Notion page or database:</label>
				<NotionPageSelect
					onChange={onPageChange}
					placeholder="Select a page or database..."
					className={styles.notionSelect}
				/>
			</div>
			<div className={styles.form}>
				<div className={styles.secondary}>
					Note: Share the parent Notion page or database with the Fellow integration for it to show up above.
				</div>
			</div>
			<div className={styles.form}>
				<IncludeAIRecapCheckbox
					hasMeetingRecordings={hasMeetingRecordings}
					checked={includeMeetingRecap}
					onCheckedChange={onIncludeMeetingRecapChange}
				/>
			</div>
		</>
	);

	const loadingBody = (
		<>
			<LoadingSpinner size="xs" className={styles.loader} />
			<div className={styles.loadingText}>
				Exporting to Notion.
				<br />
				This may take a moment...
			</div>
		</>
	);

	const completeBody = (
		<div className={styles.exportCompleteDiv}>
			<NotionLogo className={styles.exportLogo} />
			<div className={styles.exportComplete}>Export complete</div>
			<div className={styles.exportTitle}>{title}</div>
		</div>
	);

	const errorBody = (
		<div className={styles.error}>
			<FontAwesomeIcon icon={faExclamationCircle} />
			Sorry, something went wrong and we weren{"'"}t able to export this note.
		</div>
	);

	const timeoutBody = (
		<div className={styles.timeout}>
			<FontAwesomeIcon icon={faHourglassHalf} />
			<h4>Timeout!</h4>
			Check if your note titled <strong>{title}</strong> has made it to Notion — if not, please try again later.
		</div>
	);

	const renderModalBody = () => {
		if (view == "form") {
			return formBody;
		} else if (view == "loading") {
			return loadingBody;
		} else if (view == "complete") {
			return completeBody;
		} else if (view == "error") {
			return errorBody;
		} else if (view == "timeout") {
			return timeoutBody;
		}
		return;
	};

	const renderModalFooter = () => {
		if (view == "form") {
			return (
				<Modal.Footer>
					<Button size="normal" variant="secondary" key="cancel" onClick={onClose}>
						Cancel
					</Button>
					<Button size="normal" variant="primary" disabled={notionPage === undefined} onClick={startExport}>
						Export
					</Button>
				</Modal.Footer>
			);
		} else if (view == "complete") {
			return (
				<Modal.Footer>
					<Button variant="primary" size="normal" onClick={openNotionURL}>
						View in Notion
					</Button>
				</Modal.Footer>
			);
		}
		return;
	};

	const noFooter = view == "loading" || view == "error";

	return (
		<>
			<Modal.Body className={cx(styles.body, { [styles.noFooter]: noFooter })}>{renderModalBody()}</Modal.Body>
			{renderModalFooter()}
		</>
	);
};

export default NotionSendNotesModal;
